import { getDocument } from '@atlaskit/browser-apis';

/**
 * Sets the URL fragment and notifies `useScrollToHash` to scroll to it,
 * without a route change. `history.replaceState` (matching `clearHash`) adds
 * no history entry, and the `hashchange` is dispatched unconditionally rather
 * than relying on the native setter, which is a silent no-op when the
 * fragment is already `hash` — otherwise a second click on an in-page link
 * whose target the visitor has since scrolled away from would do nothing.
 */
export const setHash = (hash: string): void => {
	const view = getDocument()?.defaultView;
	if (!view) {
		return;
	}
	const { pathname, search } = view.location;
	// Read before `replaceState`, which is what makes `location.href` the new
	// URL. A `HashChangeEvent` rather than a bare `Event` so the two URLs the
	// event is specified to carry are actually on it. They are equal on a
	// repeat click, which is the case the unconditional dispatch above exists
	// to serve; a listener that diffs them will correctly see no change, while
	// `useScrollToHash` re-reads the live hash and scrolls regardless.
	const oldURL = view.location.href;
	view.history.replaceState(view.history.state, '', `${pathname}${search}#${hash}`);
	view.dispatchEvent(
		new view.HashChangeEvent('hashchange', { oldURL, newURL: view.location.href }),
	);
};
